import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import moment from "moment";
import * as React from "react";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { useLocation, useParams } from "react-router";
import { bindActionCreators } from "redux";
import { InlineLoading } from "src/components/common";
import ToggleButton from "src/components/common/Buttons/ToggleButton";
import Tree, { ITree } from "src/components/common/Tree/Tree";
import { Link } from "src/components/routing";
import { FontAwesomeIcons } from "src/constants/fontAwesomeIcons";
import {
    GroupTrialLaboratoryPersonnelsBreadcrumbs,
    TrialGroupLaboratoryPersonnelsBreadcrumbs,
} from "src/helpers/breadcrumbHelper";
import useNavigate from "src/hooks/useNavigate";
import * as NavigationActions from "../../../actions/navigation";
import * as TrialGroupLaboratoryActions from "../../../actions/trialGroupLaboratory";
import * as TrialGroupLaboratoryPersonnelActions from "../../../actions/trialGroupLaboratoryPersonnels";
import { RestrictedLayout } from "../../../components/layouts/index";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import * as AuthenticationHelper from "../../../helpers/authentication";
import {
    GroupTrialLabSubMenu,
    TrialGroupLabSubMenu,
} from "../../../helpers/subMenuHelper";
import { ITmdState } from "../../../reducers/index";

import "./TrialGroupLaboratoryPersonnelsPage.scss";

interface TrialGroupLaboratoryPersonnelsPageProps {
    trialGroupLaboratory: Dtos.TrialGroupLaboratory;
    loadingTrialGroupLaboratory: boolean;
    loadTrialGroupLaboratorySuccess: boolean;
    loadTrialGroupLaboratoryFailure: boolean;
    loadTrialGroupLaboratoryErrorCode: string;
    trialGroupLaboratoryPersonnel: Dtos.TrialGroupLaboratoryPersonnel[];
    loadingTrialGroupLaboratoryPersonnel: boolean;
    loadTrialGroupLaboratoryPersonnelSuccess: boolean;
    loadTrialGroupLaboratoryPersonnelFailure: boolean;
    loadTrialGroupLaboratoryPersonnelErrorCode: string;
    permissions?: Dtos.PersonnelTmdPermissions[];
    clearTrialGroupLaboratory: TrialGroupLaboratoryActions.ITrialGroupLaboratoryClearActionCreator;
    loadTrialGroupLaboratory: TrialGroupLaboratoryActions.ITrialGroupLaboratoryLoadByIdActionCreator;
    clearTrialGroupLaboratoryPersonnel: TrialGroupLaboratoryPersonnelActions.ITrialGroupLaboratoryPersonnelClearActionCreator;
    loadTrialGroupLaboratoryPersonnel: TrialGroupLaboratoryPersonnelActions.ITrialGroupLaboratoryPersonnelLoadByLaboratoryIdActionCreator;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
}

const TrialGroupLaboratoryPersonnelsPage: FC<
    TrialGroupLaboratoryPersonnelsPageProps
> = ({
    trialGroupLaboratory,
    loadingTrialGroupLaboratory,
    loadTrialGroupLaboratorySuccess,
    loadTrialGroupLaboratoryFailure,
    loadTrialGroupLaboratoryErrorCode,
    trialGroupLaboratoryPersonnel,
    loadingTrialGroupLaboratoryPersonnel,
    loadTrialGroupLaboratoryPersonnelSuccess,
    loadTrialGroupLaboratoryPersonnelFailure,
    loadTrialGroupLaboratoryPersonnelErrorCode,
    permissions,
    clearTrialGroupLaboratory,
    loadTrialGroupLaboratory,
    clearTrialGroupLaboratoryPersonnel,
    loadTrialGroupLaboratoryPersonnel,
    setBreadcrumbs,
    setDrawerItems
}) => {
    //
    // -- Params --
    const { trialId } = useParams<{ trialId: string }>();
    const { groupId } = useParams<{ groupId: string }>();
    const { trialGroupId } = useParams<{ trialGroupId: string }>();
    const { trialGroupLaboratoryId } = useParams<{
        trialGroupLaboratoryId: string;
    }>();
    const trialIdNumber = parseInt(trialId, 10);
    const groupIdNumber = parseInt(groupId, 10);
    const trialGroupIdNumber = parseInt(trialGroupId, 10);
    const trialGroupLaboratoryIdNumber = parseInt(trialGroupLaboratoryId, 10);

    //
    // -- Hooks --
    const navigate = useNavigate();
    const location = useLocation();

    //
    // -- States --
    const [showCeased, setShowCeased] = useState<boolean>(false);
    const [treeData, setTreeData] = useState<ITree | null>(null);
    const [initalizedTreeData, setInitalizedTreeData] =
        useState<boolean>(false);
    const [toggleState, setToggleState] = useState<string[]>([]);
    const [creatingTree, setCreatingTree] = useState<boolean>(false);

    //
    // -- Variables --
    const showPersonnels =
        !loadingTrialGroupLaboratory &&
        loadTrialGroupLaboratorySuccess &&
        !loadingTrialGroupLaboratoryPersonnel &&
        loadTrialGroupLaboratoryPersonnelSuccess && 
        !creatingTree;


    // Load Laboratory
    useEffect(() => {
        if (
            trialIdNumber &&
            trialGroupIdNumber &&
            trialGroupLaboratoryIdNumber &&
            !trialGroupLaboratory
        ) {
            const baseRoute = location?.pathname?.split("/")[1];

            const breadcrumbSetter =
                baseRoute === "trials"
                    ? TrialGroupLaboratoryPersonnelsBreadcrumbs
                    : GroupTrialLaboratoryPersonnelsBreadcrumbs;

            setBreadcrumbs(
                breadcrumbSetter(
                    trialIdNumber,
                    groupIdNumber,
                    trialGroupIdNumber,
                    trialGroupLaboratoryIdNumber
                )
            );

            setTreeData(null);

            loadTrialGroupLaboratory(trialGroupLaboratoryIdNumber);
            loadTrialGroupLaboratoryPersonnel(trialGroupLaboratoryIdNumber);
        }
    }, [
        trialIdNumber,
        groupIdNumber,
        trialGroupIdNumber,
        trialGroupLaboratoryIdNumber,
        trialGroupLaboratory,
        permissions,
        setDrawerItems,
        setBreadcrumbs,
        loadTrialGroupLaboratory,
        TrialGroupLaboratoryPersonnelsBreadcrumbs,
        GroupTrialLaboratoryPersonnelsBreadcrumbs,
        loadTrialGroupLaboratoryPersonnel,
        location,
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadTrialGroupLaboratoryFailure) {
            navigate(`/error/${loadTrialGroupLaboratoryErrorCode}`);
        }
        if (loadTrialGroupLaboratoryPersonnelFailure) {
            navigate(`/error/${loadTrialGroupLaboratoryPersonnelErrorCode}`);
        }
    }, [
        loadTrialGroupLaboratoryFailure,
        loadTrialGroupLaboratoryErrorCode,
        loadTrialGroupLaboratoryPersonnelFailure,
        loadTrialGroupLaboratoryPersonnelErrorCode,
        navigate,
    ]);

    // Update breadcrumbs
    useEffect(() => {
        if (
            trialIdNumber &&
            trialGroupIdNumber &&
            trialGroupLaboratoryIdNumber &&
            trialGroupLaboratory
        ) {
            const baseRoute = location?.pathname?.split("/")[1];

            const drawerItemSetter =
                baseRoute === "trials"
                    ? TrialGroupLabSubMenu
                    : GroupTrialLabSubMenu;

            const breadcrumbSetter =
                baseRoute === "trials"
                    ? TrialGroupLaboratoryPersonnelsBreadcrumbs
                    : GroupTrialLaboratoryPersonnelsBreadcrumbs;

            setDrawerItems(drawerItemSetter(trialGroupLaboratory, permissions));
            setBreadcrumbs(
                breadcrumbSetter(
                    trialIdNumber,
                    groupIdNumber,
                    trialGroupIdNumber,
                    trialGroupLaboratoryIdNumber,
                    trialGroupLaboratory?.trialGroup?.trial?.studyAcronym,
                    trialGroupLaboratory?.trialGroup?.group?.name,
                    trialGroupLaboratory?.laboratory?.name
                )
            );
        }
    }, [
        trialIdNumber,
        groupIdNumber,
        trialGroupIdNumber,
        trialGroupLaboratoryIdNumber,
        trialGroupLaboratory,
        permissions,
        setDrawerItems,
        setBreadcrumbs,
        TrialGroupLabSubMenu,
        GroupTrialLabSubMenu,
        TrialGroupLaboratoryPersonnelsBreadcrumbs,
        GroupTrialLaboratoryPersonnelsBreadcrumbs,
    ]);

    // Cleanup effect to clear the laboratory on component unmount
    useEffect(() => {
        return () => {
            setTreeData(null);
            clearTrialGroupLaboratory();
            clearTrialGroupLaboratoryPersonnel();
        };
    }, [clearTrialGroupLaboratory, clearTrialGroupLaboratoryPersonnel, setTreeData]);

    const onChange = (newValue) => {
        setInitalizedTreeData(false);
        setShowCeased(newValue);
    };

    const generateCreateLink = (
        trialIdNumber,
        groupIdNumber,
        trialGroupIdNumber,
        trialGroupLaboratoryIdNumber
    ) => {
        const baseRoute = location?.pathname?.split("/")[1];

        return baseRoute === "trials"
            ? `/trials/${trialIdNumber}/groups/${groupIdNumber}/trialgroup/${trialGroupIdNumber}/labs/${trialGroupLaboratoryIdNumber}/personnel/create`
            : `/groups/${groupIdNumber}/trials/${trialIdNumber}/trialgroup/${trialGroupIdNumber}/labs/${trialGroupLaboratoryIdNumber}/personnel/create`;
    };

    // Create tree
    useEffect(() => {
        if (trialGroupLaboratoryPersonnel && !initalizedTreeData) {
            setCreatingTree(true);

            // If no trial group personnel return
            if (trialGroupLaboratoryPersonnel?.length == 0) {
                setCreatingTree(false);
                return;
            }

            let uniqueRoles: Dtos.Role[] = [];
            if (showCeased) {
                uniqueRoles = Array.from(
                    new Map(
                        trialGroupLaboratoryPersonnel?.map((x) => [
                            x.role.id,
                            x.role,
                        ])
                    ).values()
                );
            } else {
                uniqueRoles = Array.from(
                    new Map(
                        trialGroupLaboratoryPersonnel
                            ?.filter((x) => !x.dateCeased)
                            .map((x) => [x.role.id, x.role])
                    ).values()
                );
            }

            const createChildNodes = (roleId, showCeased) => {
                let children = [];

                if (showCeased) {
                    children =
                        trialGroupLaboratoryPersonnel
                            ?.filter((y) => y.roleId == roleId)
                            ?.sort((a, b) => a?.id - b?.id)
                            ?.map((y) => ({
                                id: `personnel-${y.id}`,
                                data: (
                                    <div
                                        className={`form-row ${
                                            y?.dateCeased ? "ceased" : ""
                                        }`}
                                    >
                                        <div className="col-1">
                                            <FontAwesomeIcon
                                                icon={
                                                    FontAwesomeIcons.Duotone
                                                        .USER
                                                }
                                            />
                                        </div>
                                        <div className="col">
                                            {
                                                y?.personnel
                                                    ?.displayWithSalutation
                                            }
                                        </div>
                                        <div className="col">
                                            {y?.dateStarted
                                                ? moment(
                                                        y?.dateStarted,
                                                        "YYYY-MM-DD HH:mm:ss.SSSSSSS Z"
                                                    ).format("DD/MM/YYYY")
                                                : ""}
                                        </div>
                                        <div className="col">
                                            {y?.dateCeased
                                                ? moment(
                                                        y?.dateCeased,
                                                        "YYYY-MM-DD HH:mm:ss.SSSSSSS Z"
                                                    ).format("DD/MM/YYYY")
                                                : ""}
                                        </div>
                                    </div>
                                ),
                                icon: null,
                                collapsible: false,
                            })) ?? [];
                } else {
                    children =
                        trialGroupLaboratoryPersonnel
                            ?.filter((y) => y.roleId == roleId && !y.dateCeased)
                            ?.sort((a, b) => a?.id - b?.id)
                            ?.map((y) => ({
                                id: `personnel-${y?.id}`,
                                data: (
                                    <div className="form-row">
                                        <div className="col-1">
                                            <FontAwesomeIcon
                                                icon={
                                                    FontAwesomeIcons.Duotone
                                                        .USER
                                                }
                                            />
                                        </div>
                                        <div className="col">
                                            {
                                                y?.personnel
                                                    ?.displayWithSalutation
                                            }
                                        </div>
                                        <div className="col">
                                            {y?.dateStarted
                                                ? moment(
                                                        y?.dateStarted,
                                                        "YYYY-MM-DD HH:mm:ss.SSSSSSS Z"
                                                    ).format("DD/MM/YYYY")
                                                : ""}
                                        </div>
                                        <div className="col">
                                            {y?.dateCeased
                                                ? moment(
                                                        y?.dateCeased,
                                                        "YYYY-MM-DD HH:mm:ss.SSSSSSS Z"
                                                    ).format("DD/MM/YYYY")
                                                : ""}
                                        </div>
                                    </div>
                                ),
                                icon: null,
                                collapsible: false,
                            })) ?? [];
                }

                // Add table header
                if (children?.length > 0) {
                    children.unshift({
                        id: `tableheader`,
                        data: (
                            <div className="form-row">
                                <div className="col-1"></div>
                                <div className="col">
                                    <strong>Name</strong>
                                </div>
                                <div className="col">
                                    <strong>Date Started</strong>
                                </div>
                                <div className="col">
                                    <strong>Date Ceased</strong>
                                </div>
                            </div>
                        ),
                        icon: null,
                        collapsible: false,
                    });
                }

                return children;
            };

            let treeNodes =
                uniqueRoles
                    ?.sort((a, b) => a?.value?.localeCompare(b?.value))
                    ?.map((x) => ({
                        id: `role-${x.id}`,
                        data: (
                            <div className="d-flex align-items-center">
                                <FontAwesomeIcon
                                    icon={FontAwesomeIcons.Duotone.ROLE}
                                />
                                <span
                                    className="pl-4 mb-0"
                                    style={{ width: "275px" }}
                                >
                                    {x?.value}
                                </span>
                                <div
                                    className="d-flex align-items-center justify-content-center"
                                    style={{ width: "50px" }}
                                >
                                    <span className="badge badge-pill badge-secondary ml-4 mb-0">
                                        {
                                            trialGroupLaboratoryPersonnel?.filter(
                                                (y) =>
                                                    showCeased
                                                        ? y.roleId == x.id
                                                        : y.roleId == x.id &&
                                                            !y.dateCeased
                                            )?.length
                                        }
                                    </span>
                                </div>
                            </div>
                        ),
                        icon: null,
                        collapsible: true,
                        subTree: {
                            children: [...createChildNodes(x.id, showCeased)],
                        },
                    })) ?? [];

            // Initialize tree toggle
            const expanded = treeNodes.map((z) => z.id);

            setToggleState(expanded);
            setTreeData({ children: treeNodes });
            setInitalizedTreeData(true);
            setCreatingTree(false);
        }
    }, [
        trialGroupLaboratoryPersonnel,
        initalizedTreeData,
        treeData,
        setTreeData,
        setToggleState,
        setInitalizedTreeData,
        showCeased,
        setCreatingTree,
    ]);

    const handleToggle = useCallback(
        (id: string, navigateFlag?: boolean) => {
            if (navigateFlag) {
                if (
                    id.includes("personnel") &&
                    AuthenticationHelper.hasPermission(
                        permissions,
                        Dtos.Permission.TrialPersonnelRoleView
                    )
                ) {
                    navigate(
                        `/trials/${trialIdNumber}/groups/${groupIdNumber}/trialgroup/${trialGroupIdNumber}/labs/${trialGroupLaboratoryIdNumber}/personnel/${id.replace(
                            "personnel-",
                            ""
                        )}/details`
                    );
                }
                if (id.includes("role")) {
                    setToggleState((prev) =>
                        prev.includes(id)
                            ? prev.filter((nodeId) => nodeId !== id)
                            : [...prev, id]
                    );
                }
            } else {
                setToggleState((prev) =>
                    prev.includes(id)
                        ? prev.filter((nodeId) => nodeId !== id)
                        : [...prev, id]
                );
            }
        },
        [
            setToggleState,
            navigate,
            trialIdNumber,
            groupIdNumber,
            trialGroupIdNumber,
            trialGroupLaboratoryIdNumber,
            permissions,
        ]
    );

    // Refresh tree
    useEffect(() => {
        if (trialGroupLaboratoryPersonnel) {
            setInitalizedTreeData(false);
        }
    }, [trialGroupLaboratoryPersonnel, setInitalizedTreeData]);

    const renderContent = useMemo(() => {
        return (
            <div>
                <div className="row">
                    <div className="col-6 mb-2">
                        <h1>Personnel</h1>
                    </div>
                    <div className="col-6 d-flex align-items-center justify-content-end mb-2">
                        <ToggleButton
                            id="toggle-button-ceased"
                            checked={showCeased}
                            onChange={onChange}
                            labelA="Hide ceased personnel"
                            labelB="Show ceased personnel"
                            style={{ minWidth: "200px" }}
                        />
                        <a
                            className="btn btn-info mr-2"
                            target="_blank"
                            href={encodeURI(
                                `/api/personnelrole/export/${trialGroupLaboratory?.trialGroup?.trialId}/0/${trialGroupLaboratory?.trialGroup?.groupId}/${trialGroupLaboratory?.laboratoryId}`
                            )}
                        >
                            <FontAwesomeIcon icon={FontAwesomeIcons.Solid.CLOUD_DOWNLOAD} fixedWidth />
                        </a>
                        {AuthenticationHelper.hasPermission(
                            permissions,
                            Dtos.Permission.TrialGroupLabCreate
                        ) && (
                            <Link
                                className={"btn btn-primary"}
                                url={generateCreateLink(
                                    trialIdNumber,
                                    groupIdNumber,
                                    trialGroupIdNumber,
                                    trialGroupLaboratoryIdNumber
                                )}
                            >
                                Create
                            </Link>
                        )}
                    </div>
                </div>
                {!showPersonnels ? (
                    <InlineLoading absoluteCenter={true} size={50} />
                ) : null}
                {showPersonnels ? (
                    <>
                        {treeData && treeData?.children?.length > 0 ? (
                            <Tree
                                treeData={treeData}
                                onToggle={handleToggle}
                                toggleState={toggleState}
                            />
                        ) : (
                            <div className="card card-shadow p-3">
                                <div className="alert alert-info d-flex mb-0">
                                    <div className="mr-2">
                                        <FontAwesomeIcon
                                            icon={
                                                FontAwesomeIcons.Solid
                                                    .INFO_SQUARE
                                            }
                                            fixedWidth
                                        />
                                    </div>
                                    <span>
                                        There are currently no trial group
                                        laboratory personnel.
                                    </span>
                                </div>
                            </div>
                        )}
                    </>
                ) : null}
            </div>
        );
    }, [
        AuthenticationHelper,
        permissions,
        showPersonnels,
        treeData,
        handleToggle,
        toggleState,
        onChange,
        trialIdNumber,
        groupIdNumber,
        trialGroupIdNumber,
        trialGroupLaboratoryIdNumber,
        generateCreateLink,
        showCeased,
    ]);

    return (
        <RestrictedLayout
            permission={Dtos.Permission.TrialGroupLabView}
            loading={loadingTrialGroupLaboratory || loadingTrialGroupLaboratoryPersonnel}
        >
            {renderContent}
        </RestrictedLayout>
    );
};

const mapStateToProps = (
    state: ITmdState
): Partial<TrialGroupLaboratoryPersonnelsPageProps> => {
    let trialGroupLaboratory: Dtos.TrialGroupLaboratory | undefined = !(
        state.trialGroupLaboratory.formData instanceof Array
    )
        ? state.trialGroupLaboratory.formData
        : undefined;
    let trialGroupLaboratoryPersonnel:
            | Dtos.TrialGroupLaboratoryPersonnel[] | undefined = (state.trialGroupLaboratoryPersonnel.formData instanceof Array)
            ? state.trialGroupLaboratoryPersonnel.formData
            : undefined;
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        trialGroupLaboratory: trialGroupLaboratory!,
        loadingTrialGroupLaboratory:
            state.trialGroupLaboratory.loadState &&
            state.trialGroupLaboratory.loadState.status == RequestState.Pending,
        loadTrialGroupLaboratorySuccess:
            state.trialGroupLaboratory.loadState &&
            state.trialGroupLaboratory.loadState.status == RequestState.Success,
        loadTrialGroupLaboratoryFailure:
            state.trialGroupLaboratory.loadState &&
            state.trialGroupLaboratory.loadState.status == RequestState.Failure,
        loadTrialGroupLaboratoryErrorCode:
            state.trialGroupLaboratory.loadState.errorCode,
        trialGroupLaboratoryPersonnel: trialGroupLaboratoryPersonnel,
        loadingTrialGroupLaboratoryPersonnel:
            state.trialGroupLaboratoryPersonnel.loadState &&
            state.trialGroupLaboratoryPersonnel.loadState.status ==
                RequestState.Pending,
        loadTrialGroupLaboratoryPersonnelSuccess:
            state.trialGroupLaboratoryPersonnel.loadState &&
            state.trialGroupLaboratoryPersonnel.loadState.status ==
                RequestState.Success,
        loadTrialGroupLaboratoryPersonnelFailure:
            state.trialGroupLaboratoryPersonnel.loadState &&
            state.trialGroupLaboratoryPersonnel.loadState.status ==
                RequestState.Failure,
        loadTrialGroupLaboratoryPersonnelErrorCode:
            state.trialGroupLaboratoryPersonnel.loadState.errorCode,
        permissions: permissions,
    };
};

const mapDispatchToProps = (
    dispatch
): Partial<TrialGroupLaboratoryPersonnelsPageProps> => {
    return {
        loadTrialGroupLaboratory: bindActionCreators(
            TrialGroupLaboratoryActions.LoadTrialGroupLaboratoryById,
            dispatch
        ),
        clearTrialGroupLaboratory: bindActionCreators(
            TrialGroupLaboratoryActions.Clear,
            dispatch
        ),
        setBreadcrumbs: bindActionCreators(
            NavigationActions.SetBreadcrumbs,
            dispatch
        ),
        setDrawerItems: bindActionCreators(
            NavigationActions.SetDrawerItems,
            dispatch
        ),
        loadTrialGroupLaboratoryPersonnel: bindActionCreators(
            TrialGroupLaboratoryPersonnelActions.LoadTrialGroupLaboratoryPersonnelByLaboratoryId,
            dispatch
        ),
        clearTrialGroupLaboratoryPersonnel: bindActionCreators(
            TrialGroupLaboratoryPersonnelActions.Clear,
            dispatch
        )
    };
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(TrialGroupLaboratoryPersonnelsPage);
